import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChartComments } from './ChartComments';
import { supabaseClient } from '../../services/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { useChartPngRegistryActions, chartPngIdFromTitle, ChartPngPageIdOverrideContext, type ChartPngPageId } from '../../contexts/ChartPngRegistryContext';
import './ChartLayout.css';

// Grouped Chart Selector Component
interface GroupedChartSelectorProps {
  groups: {
    category: string;
    label: string;
    options: { value: string; label: string }[];
  }[];
  value: string;
  onChange: (value: string) => void;
}

const GroupedChartSelector: React.FC<GroupedChartSelectorProps> = ({ groups, value, onChange }) => {
  // Find which category the current value belongs to
  const activeCategory = useMemo(() => {
    for (const group of groups) {
      if (group.options.some(opt => opt.value === value)) {
        return group.category;
      }
    }
    return groups[0]?.category || '';
  }, [groups, value]);

  const activeGroup = groups.find(g => g.category === activeCategory);

  return (
    <div className="grouped-chart-selector">
      {/* Main category buttons */}
      <div className="chart-selector-categories">
        {groups.map(group => (
          <button
            key={group.category}
            className={`category-btn ${activeCategory === group.category ? 'active' : ''}`}
            onClick={() => {
              // Switch to first option in this category when category is clicked
              if (activeCategory !== group.category && group.options.length > 0) {
                onChange(group.options[0].value);
              }
            }}
          >
            {group.label}
          </button>
        ))}
      </div>
      {/* Sub-option buttons for active category */}
      {activeGroup && (
        <div className="chart-selector-options">
          {activeGroup.options.map(option => (
            <button
              key={option.value}
              className={`toggle-btn ${value === option.value ? 'active' : ''}`}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface ChartLayoutProps {
  title: string;
  description?: string;
  chartType?: 'line' | 'bar' | 'doughnut' | 'pie' | 'map';
  keyMetrics?: ReactNode;
  chartContent: ReactNode;
  overviewContent?: ReactNode;
  relevanceContent?: ReactNode;
  equityContent?: ReactNode;
  methodologyContent?: ReactNode;
  activeView: string;
  onViewChange: (view: string) => void;
  chartSelector?: {
    options?: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
    grouped?: boolean;
    groups?: {
      category: string;
      label: string;
      options: { value: string; label: string }[];
    }[];
  };
  className?: string;
  showRelevanceEquity?: boolean;
  layout?: 'side-by-side' | 'vertical';
  // Download functionality
  onDownloadPNG?: () => void;
  onDownloadCSV?: () => void;
  pngFilename?: string;
  csvFilename?: string;
  // Data table functionality
  dataTableContent?: ReactNode;
  showDataTable?: boolean;
  onToggleDataTable?: () => void;
  // Comments functionality
  enableComments?: boolean;
  location?: string;
  overviewSectionId?: string;
  overviewChartId?: string;
  /** Optional id for the section element; used for deep-linking from livability/equity index */
  scrollAnchorId?: string;
  /** When false, this chart does not register for the header "Download PNGs" list (e.g. when the page registers each view separately). Default true. */
  registerPng?: boolean;
  // Additional button groups (for backcasting options, etc.)
  additionalButtonGroups?: {
    label: string;
    options: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
  }[];
}

interface ChartTextOverridesContextValue {
  canEditText: boolean;
  location?: string;
  chartTitle?: string;
  overrides: Record<string, string>;
  editingKey: string | null;
  draftText: string;
  setDraftText: (value: string) => void;
  startEditing: (key: string, seedValue: string) => void;
  cancelEditing: () => void;
  saveOverride: (key: string) => void;
  removeOverride: (key: string) => void;
}

const ChartTextOverridesContext = React.createContext<ChartTextOverridesContextValue | null>(null);

const renderOverrideParagraphs = (text: string) => {
  const blocks = text.trim().split(/\n\s*\n/);
  return blocks.map((block, blockIndex) => {
    const lines = block.split('\n');
    return (
      <p key={`override-block-${blockIndex}`}>
        {lines.map((line, lineIndex) => (
          <React.Fragment key={`override-line-${blockIndex}-${lineIndex}`}>
            {line}
            {lineIndex < lines.length - 1 ? <br /> : null}
          </React.Fragment>
        ))}
      </p>
    );
  });
};

const htmlToPlainText = (value: string) =>
  value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .trim();

const extractPlainText = (node: ReactNode): string => {
  if (node === null || node === undefined || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    return node
      .map(extractPlainText)
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  if (React.isValidElement(node)) {
    return extractPlainText(node.props?.children);
  }
  return '';
};

const extractSimpleCardContent = (node: ReactNode): { title: string; body: string } => {
  let titleText = '';
  let bodyText = '';

  if (React.isValidElement(node)) {
    React.Children.forEach(node.props?.children, (child) => {
      if (!React.isValidElement(child)) return;
      const element = child as React.ReactElement<any>;
      if (element.type === 'h5' && !titleText) {
        titleText = extractPlainText(element.props?.children);
      }
      if (element.type === 'p' && !bodyText) {
        bodyText = extractPlainText(element.props?.children);
      }
    });
  }

  return { title: titleText, body: bodyText };
};

const isSimpleCardElement = (node: ReactNode): node is React.ReactElement => {
  if (!React.isValidElement(node)) return false;
  const className = node.props?.className;
  return typeof className === 'string' && className.includes('methodology-details-simple');
};

export const ChartLayout: React.FC<ChartLayoutProps> = ({
  title,
  description,
  chartType = 'line',
  keyMetrics,
  chartContent,
  overviewContent,
  relevanceContent,
  equityContent,
  methodologyContent,
  activeView,
  onViewChange,
  chartSelector,
  className = '',
  showRelevanceEquity = false,
  layout = 'side-by-side',
  onDownloadPNG,
  onDownloadCSV,
  pngFilename = 'Chart',
  csvFilename = 'Data',
  dataTableContent,
  showDataTable = false,
  onToggleDataTable,
  enableComments = false,
  location,
  scrollAnchorId,
  registerPng = true,
  additionalButtonGroups
}) => {
  const { user } = useAuth();
  const { hasAnyRole } = useUserRole();
  // Internal state for managing comments toggle
  const [showComments, setShowComments] = useState(false);
  const [commentsCount, setCommentsCount] = useState(0);
  const [textOverrides, setTextOverrides] = useState<Record<string, string>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');

  const normalizeLocation = (value?: string | null) =>
    (value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const userLocation = normalizeLocation(user?.user_metadata?.specific_location);
  const chartLocation = normalizeLocation(location);
  const isOwnLocation = !!userLocation && !!chartLocation && userLocation === chartLocation;
  const canEditText = hasAnyRole(['local_govt_admin', 'super_admin']) && (hasAnyRole(['super_admin']) || isOwnLocation);

  // Fetch comments count
  useEffect(() => {
    if (enableComments) {
      const fetchCount = async () => {
        const { count } = await supabaseClient
          .from('community_comments')
          .select('*', { count: 'exact', head: true })
          .eq('chart_context', title)
          .is('parent_id', null);
        setCommentsCount(count || 0);
      };
      fetchCount();
    }
  }, [enableComments, title]);

  const loadOverrides = useCallback(async () => {
    if (!location || !title) return;
    const { data, error } = await supabaseClient
      .from('chart_text_overrides')
      .select('section, content')
      .eq('location_name', location)
      .eq('chart_title', title);

    if (error) {
      console.error('Error fetching text overrides:', error);
      return;
    }

    const nextOverrides: Record<string, string> = {};
    (data || []).forEach((row: any) => {
      if (row.section && row.content) {
        nextOverrides[row.section] = row.content;
      }
    });
    setTextOverrides(nextOverrides);
  }, [location, title]);

  useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  // Register with chart PNG registry so the download modal can list and trigger this chart's export (unless the page registers each view separately)
  const { register: registerPngChart, unregister: unregisterPngChart } = useChartPngRegistryActions();
  const chartId = chartPngIdFromTitle(title);
  const pageIdOverride = useContext(ChartPngPageIdOverrideContext);
  const routerLocation = useLocation();
  const downloadPageId = useMemo((): ChartPngPageId | undefined => {
    if (pageIdOverride) return pageIdOverride;
    const pathname = routerLocation?.pathname ?? '';
    const segments = pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (last === 'other') return 'waste';
    if (['transportation', 'buildings', 'people', 'overview'].includes(last)) return last as ChartPngPageId;
    return 'overview';
  }, [pageIdOverride, routerLocation?.pathname]);
  useEffect(() => {
    if (!registerPng || !onDownloadPNG) return;
    registerPngChart(chartId, title, onDownloadPNG, downloadPageId);
    return () => unregisterPngChart(chartId);
  }, [registerPng, chartId, title, onDownloadPNG, downloadPageId, registerPngChart, unregisterPngChart]);

  const startEditing = useCallback((key: string, seedValue: string) => {
    setEditingKey(key);
    setDraftText(textOverrides[key] ?? seedValue ?? '');
  }, [textOverrides]);

  const cancelEditing = useCallback(() => {
    setEditingKey(null);
    setDraftText('');
  }, []);

  const saveOverride = useCallback(async (key: string) => {
    if (!location || !title || !user) return;
    if (!draftText.trim()) return;

    const confirmSave = window.confirm(
      'This will permanently replace the generated narrative with your custom text. Continue?'
    );
    if (!confirmSave) return;

    const { error } = await supabaseClient
      .from('chart_text_overrides')
      .upsert({
        location_name: location,
        chart_title: title,
        section: key,
        content: draftText.trim(),
        updated_by: user.id,
        updated_at: new Date().toISOString()
      }, { onConflict: 'location_name,chart_title,section' });

    if (error) {
      console.error('Error saving text override:', error);
      return;
    }

    setTextOverrides((prev) => ({ ...prev, [key]: draftText.trim() }));
    setEditingKey(null);
    setDraftText('');
  }, [location, title, user, draftText]);

  const removeOverride = useCallback(async (key: string) => {
    if (!location || !title || !user) return;

    const confirmRemove = window.confirm('Remove the custom text and restore the default narrative?');
    if (!confirmRemove) return;

    const { error } = await supabaseClient
      .from('chart_text_overrides')
      .delete()
      .eq('location_name', location)
      .eq('chart_title', title)
      .eq('section', key);

    if (error) {
      console.error('Error removing text override:', error);
      return;
    }

    setTextOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [location, title, user]);

  const contextValue = useMemo<ChartTextOverridesContextValue>(
    () => ({
      canEditText,
      location: location || undefined,
      chartTitle: title,
      overrides: textOverrides,
      editingKey,
      draftText,
      setDraftText,
      startEditing,
      cancelEditing,
      saveOverride,
      removeOverride
    }),
    [canEditText, location, title, textOverrides, editingKey, draftText, removeOverride, saveOverride, startEditing, cancelEditing]
  );

  // Handle custom data table toggle - close comments if open
  const handleToggleDataTable = () => {
    setShowComments(false);
    if (onToggleDataTable) {
      onToggleDataTable();
    }
  };

  // Handle comments toggle - close data table if open
  const handleToggleComments = () => {
    if (showDataTable && onToggleDataTable) {
      onToggleDataTable();
    }
    setShowComments(!showComments);
  };

  return (
    <ChartTextOverridesContext.Provider value={contextValue}>
      <div className={`chart-section ${className}`} id={scrollAnchorId || undefined}>
        <div className="chart-content">
        <div className="chart-title-section">
          <h3>{title}</h3>
          {description && (
            <p 
              className="chart-description-text" 
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          
          {/* Key Metrics - right after title */}
          {keyMetrics && (
            <div className="key-metrics-after-title">
              {keyMetrics}
            </div>
          )}
        </div>
        
        {/* Conditional Layout: Side-by-side or Vertical */}
        {layout === 'side-by-side' ? (
          /* Side-by-side layout: Chart on left, Narrative on right */
          <div className={`chart-content-layout ${!(overviewContent || relevanceContent || equityContent || methodologyContent) ? 'single-column' : ''}`}>
            {/* Left Column - Chart */}
            <div className="chart-left-side">
              
              {/* Chart Type Selector (optional) - positioned directly above chart */}
              {chartSelector && (
                chartSelector.grouped && chartSelector.groups ? (
                  <GroupedChartSelector
                    groups={chartSelector.groups}
                    value={chartSelector.value}
                    onChange={chartSelector.onChange}
                  />
                ) : chartSelector.options ? (
                  <div className="chart-selector">
                    {chartSelector.options.map(option => (
                      <button
                        key={option.value}
                        className={`toggle-btn ${chartSelector.value === option.value ? 'active' : ''}`}
                        onClick={() => chartSelector.onChange(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null
              )}
              
              {/* Additional button groups (second row) - now dropdowns */}
              {additionalButtonGroups && additionalButtonGroups.length > 0 && (
                <div className="additional-button-groups">
                  {additionalButtonGroups.map((group, idx) => (
                    <div key={idx} className="additional-button-group">
                      <label className="additional-button-group-label">{group.label}:</label>
                      <select
                        className="additional-button-group-select"
                        value={group.value}
                        onChange={(e) => group.onChange(e.target.value)}
                      >
                        {group.options.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Chart container - provides consistent styling and loading placeholder */}
              <div className="chart-container">
                {chartContent}
              </div>

              {/* Buttons row - inside chart-left-side so they align with the chart */}
              {(onDownloadPNG || onDownloadCSV || onToggleDataTable || enableComments) && (
                <div className="chart-buttons-row chart-left-buttons">
                  <div className="left-buttons-group">
                    {onToggleDataTable && (
                      <button 
                        className="customize-data-btn"
                        onClick={handleToggleDataTable}
                        title="Customize Data"
                      >
                        {showDataTable ? 'Hide Data Table' : 'Customize Data'}
                      </button>
                    )}
                    {enableComments && (
                      <button 
                        className="comments-toggle-btn"
                        onClick={handleToggleComments}
                        title="View Comments"
                      >
                        💬 Comments {commentsCount > 0 && `(${commentsCount})`}
                      </button>
                    )}
                  </div>
                  {(onDownloadPNG || onDownloadCSV) && (
                    <div className="download-buttons-group">
                      {onDownloadPNG && (
                        <button 
                          className="chart-download-btn"
                          onClick={onDownloadPNG}
                          title={`Download ${pngFilename} as PNG`}
                        >
                          🖼️ PNG
                        </button>
                      )}
                      {onDownloadCSV && (
                        <button 
                          className="chart-download-btn"
                          onClick={onDownloadCSV}
                          title={`Download ${csvFilename} as CSV`}
                        >
                          📊 CSV
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Data table and comments - inside chart-left-side */}
              {showDataTable && <div className="data-table-wrapper">{dataTableContent}</div>}
              {showComments && <div className="comments-wrapper"><ChartComments chartName={title} location={location} visible={showComments} showToggle={false} /></div>}
            </div>

            {/* Right Column - Buttons and Content - Only render if there's content */}
            {(overviewContent || relevanceContent || equityContent || methodologyContent) ? (
              <div className="chart-right-side">
                {/* Toggle Buttons */}
                <div className="toggle-buttons">
                  {overviewContent && (
                    <button 
                      className={`toggle-btn ${activeView === 'overview' ? 'active' : ''}`}
                      onClick={() => onViewChange('overview')}
                    >
                      Overview
                    </button>
                  )}
                  {showRelevanceEquity && (
                    <>
                      <button 
                        className={`toggle-btn ${activeView === 'relevance' ? 'active' : ''}`}
                        onClick={() => onViewChange('relevance')}
                      >
                        Relevance
                      </button>
                      <button 
                        className={`toggle-btn ${activeView === 'equity' ? 'active' : ''}`}
                        onClick={() => onViewChange('equity')}
                      >
                        Equity
                      </button>
                    </>
                  )}
                  {methodologyContent && (
                    <button 
                      className={`toggle-btn ${activeView === 'methodology' ? 'active' : ''}`}
                      onClick={() => onViewChange('methodology')}
                    >
                      Methodology
                    </button>
                  )}
                </div>
                
                {/* Content Sections */}
                <div className="content-sections">
                  {/* Overview Section */}
                  {overviewContent && (
                    <div className={`content-section ${activeView === 'overview' ? '' : 'hidden'}`}>
                      {overviewContent}
                    </div>
                  )}
                  
                  {/* Relevance Section */}
                  {showRelevanceEquity && (
                    <div className={`content-section ${activeView === 'relevance' ? '' : 'hidden'}`}>
                      {relevanceContent}
                    </div>
                  )}
                  
                  {/* Equity Section */}
                  {showRelevanceEquity && (
                    <div className={`content-section ${activeView === 'equity' ? '' : 'hidden'}`}>
                      {equityContent}
                    </div>
                  )}
                  
                  {/* Methodology Section */}
                  {methodologyContent && (
                    <div className={`content-section ${activeView === 'methodology' ? '' : 'hidden'}`}>
                      {methodologyContent}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          /* Vertical layout: Middle: Chart, Bottom: Narrative */
          <div className="chart-vertical-layout">
            {/* Middle Section: Chart */}
            <div className="chart-section">
              {/* Chart Type Selector (optional) - positioned directly above chart */}
              {chartSelector && (
                chartSelector.grouped && chartSelector.groups ? (
                  <GroupedChartSelector
                    groups={chartSelector.groups}
                    value={chartSelector.value}
                    onChange={chartSelector.onChange}
                  />
                ) : chartSelector.options ? (
                  <div className="chart-selector">
                    {chartSelector.options.map(option => (
                      <button
                        key={option.value}
                        className={`toggle-btn ${chartSelector.value === option.value ? 'active' : ''}`}
                        onClick={() => chartSelector.onChange(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null
              )}
              
              {/* Additional button groups (second row) - now dropdowns */}
              {additionalButtonGroups && additionalButtonGroups.length > 0 && (
                <div className="additional-button-groups">
                  {additionalButtonGroups.map((group, idx) => (
                    <div key={idx} className="additional-button-group">
                      <label className="additional-button-group-label">{group.label}:</label>
                      <select
                        className="additional-button-group-select"
                        value={group.value}
                        onChange={(e) => group.onChange(e.target.value)}
                      >
                        {group.options.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Chart container - provides consistent styling and loading placeholder */}
              <div className="chart-container">
                {chartContent}
              </div>
            </div>
            
            {/* Bottom Section: Toggle Buttons + Content */}
            <div className="narrative-section">
              {/* Toggle Buttons */}
              <div className="toggle-buttons">
                {overviewContent && (
                  <button 
                    className={`toggle-btn ${activeView === 'overview' ? 'active' : ''}`}
                    onClick={() => onViewChange('overview')}
                  >
                    Overview
                  </button>
                )}
                {showRelevanceEquity && (
                  <>
                    <button 
                      className={`toggle-btn ${activeView === 'relevance' ? 'active' : ''}`}
                      onClick={() => onViewChange('relevance')}
                    >
                      Relevance
                    </button>
                    <button 
                      className={`toggle-btn ${activeView === 'equity' ? 'active' : ''}`}
                      onClick={() => onViewChange('equity')}
                    >
                      Equity
                    </button>
                  </>
                )}
                {methodologyContent && (
                  <button 
                    className={`toggle-btn ${activeView === 'methodology' ? 'active' : ''}`}
                    onClick={() => onViewChange('methodology')}
                  >
                    Methodology
                  </button>
                )}
              </div>
              
              {/* Content Sections */}
              <div className="content-sections">
                {/* Overview Section */}
                {overviewContent && (
                  <div className={`content-section ${activeView === 'overview' ? '' : 'hidden'}`}>
                    {overviewContent}
                  </div>
                )}
                
                {/* Relevance Section */}
                {showRelevanceEquity && (
                  <div className={`content-section ${activeView === 'relevance' ? '' : 'hidden'}`}>
                    {relevanceContent}
                  </div>
                )}
                
                {/* Equity Section */}
                {showRelevanceEquity && (
                  <div className={`content-section ${activeView === 'equity' ? '' : 'hidden'}`}>
                    {equityContent}
                  </div>
                )}
                
                {/* Methodology Section */}
                {methodologyContent && (
                  <div className={`content-section ${activeView === 'methodology' ? '' : 'hidden'}`}>
                    {methodologyContent}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </ChartTextOverridesContext.Provider>
  );
};

// Content components for consistent structure
export const OverviewContent: React.FC<{
  title: string;
  description: string;
  children?: ReactNode;
}> = ({ title, description, children }) => {
  const ctx = React.useContext(ChartTextOverridesContext);
  const overrides = ctx?.overrides || {};
  const canEdit = !!ctx?.canEditText && !!ctx?.location;

  const titleOverride = overrides.overview_title;
  const descriptionOverride = overrides.overview_description;
  const insightsOverride = overrides.overview_insights;

  const isEditingTitle = ctx?.editingKey === 'overview_title';
  const isEditingDescription = ctx?.editingKey === 'overview_description';
  const isEditingInsights = ctx?.editingKey === 'overview_insights';

  return (
    <>
      <div className="editable-section">
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingTitle && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() => ctx?.startEditing('overview_title', titleOverride ?? title)}
                >
                  Edit title
                </button>
                {titleOverride && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => ctx?.removeOverride('overview_title')}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingTitle ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter custom title..."
              rows={2}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride('overview_title')}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <h4>{titleOverride || title}</h4>
        )}
      </div>

      <div className="editable-section">
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingDescription && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() =>
                    ctx?.startEditing(
                      'overview_description',
                      descriptionOverride ?? htmlToPlainText(description)
                    )
                  }
                >
                  Edit overview text
                </button>
                {descriptionOverride && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => ctx?.removeOverride('overview_description')}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingDescription ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter custom overview text..."
              rows={6}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride('overview_description')}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : descriptionOverride ? (
          <div className="editable-section-override">{renderOverrideParagraphs(descriptionOverride)}</div>
        ) : (
          <p dangerouslySetInnerHTML={{ __html: description }} />
        )}
      </div>

      {children && (
        <div className="key-metrics">
          <div className="editable-section">
            {canEdit && (
              <div className="editable-section-controls">
                {!isEditingInsights && (
                  <>
                    <button
                      className="edit-text-button"
                      onClick={() =>
                        ctx?.startEditing(
                          'overview_insights',
                          insightsOverride ?? extractPlainText(children)
                        )
                      }
                    >
                      Edit key insights
                    </button>
                    {insightsOverride && (
                      <button
                        className="edit-text-secondary"
                        onClick={() => ctx?.removeOverride('overview_insights')}
                      >
                        Restore default
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <h5>Key Insights:</h5>
          <div className="insights-content">
            {isEditingInsights ? (
              <div className="editable-section-editor">
                <textarea
                  className="editable-section-textarea"
                  value={ctx?.draftText || ''}
                  onChange={(e) => ctx?.setDraftText(e.target.value)}
                  placeholder="Enter custom key insights..."
                  rows={6}
                />
                <div className="editable-section-actions">
                  <button className="edit-text-button" onClick={() => ctx?.saveOverride('overview_insights')}>
                    Save
                  </button>
                  <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : insightsOverride ? (
              renderOverrideParagraphs(insightsOverride)
            ) : (
              children
            )}
          </div>
        </div>
      )}
    </>
  );
};

export const RelevanceContent: React.FC<{
  title: string;
  description: string;
  children?: ReactNode;
}> = ({ title, description, children }) => {
  const ctx = React.useContext(ChartTextOverridesContext);
  const overrides = ctx?.overrides || {};
  const canEdit = !!ctx?.canEditText && !!ctx?.location;

  const titleOverride = overrides.relevance_title;
  const descriptionOverride = overrides.relevance_description;
  const isEditingTitle = ctx?.editingKey === 'relevance_title';
  const isEditingDescription = ctx?.editingKey === 'relevance_description';
  let cardIndex = 0;

  const renderEditableCard = (cardTitle: string, cardBody: string) => {
    const index = cardIndex;
    cardIndex += 1;
    const titleKey = `relevance_card_${index}_title`;
    const bodyKey = `relevance_card_${index}_body`;
    const titleOverrideValue = overrides[titleKey];
    const bodyOverrideValue = overrides[bodyKey];
    const isEditingCardTitle = ctx?.editingKey === titleKey;
    const isEditingCardBody = ctx?.editingKey === bodyKey;

    return (
      <div className="methodology-details-simple" key={titleKey}>
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingCardTitle && !isEditingCardBody && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() => ctx?.startEditing(titleKey, titleOverrideValue ?? cardTitle)}
                >
                  Edit card title
                </button>
                <button
                  className="edit-text-button"
                  onClick={() => ctx?.startEditing(bodyKey, bodyOverrideValue ?? cardBody)}
                >
                  Edit card text
                </button>
                {(titleOverrideValue || bodyOverrideValue) && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => {
                      if (titleOverrideValue) ctx?.removeOverride(titleKey);
                      if (bodyOverrideValue) ctx?.removeOverride(bodyKey);
                    }}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingCardTitle ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter card title..."
              rows={2}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride(titleKey)}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <h5>{titleOverrideValue || cardTitle}</h5>
        )}
        {isEditingCardBody ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter card text..."
              rows={4}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride(bodyKey)}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : bodyOverrideValue ? (
          renderOverrideParagraphs(bodyOverrideValue)
        ) : (
          <p>{cardBody}</p>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="editable-section">
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingTitle && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() => ctx?.startEditing('relevance_title', titleOverride ?? title)}
                >
                  Edit title
                </button>
                {titleOverride && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => ctx?.removeOverride('relevance_title')}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingTitle ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter custom title..."
              rows={2}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride('relevance_title')}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <h4>{titleOverride || title}</h4>
        )}
      </div>

      <div className="editable-section">
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingDescription && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() =>
                    ctx?.startEditing(
                      'relevance_description',
                      descriptionOverride ?? htmlToPlainText(description)
                    )
                  }
                >
                  Edit relevance text
                </button>
                {descriptionOverride && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => ctx?.removeOverride('relevance_description')}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingDescription ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter custom relevance text..."
              rows={6}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride('relevance_description')}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : descriptionOverride ? (
          <div className="editable-section-override">{renderOverrideParagraphs(descriptionOverride)}</div>
        ) : (
          <p dangerouslySetInnerHTML={{ __html: description }} />
        )}
      </div>
      {React.Children.map(children, (child) => {
        if (!isSimpleCardElement(child)) return child;
        const { title: cardTitle, body: cardBody } = extractSimpleCardContent(child);
        return renderEditableCard(cardTitle, cardBody);
      })}
    </>
  );
};

export const EquityContent: React.FC<{
  title: string;
  description: string;
  children?: ReactNode;
}> = ({ title, description, children }) => {
  const ctx = React.useContext(ChartTextOverridesContext);
  const overrides = ctx?.overrides || {};
  const canEdit = !!ctx?.canEditText && !!ctx?.location;

  const titleOverride = overrides.equity_title;
  const descriptionOverride = overrides.equity_description;
  const isEditingTitle = ctx?.editingKey === 'equity_title';
  const isEditingDescription = ctx?.editingKey === 'equity_description';
  let cardIndex = 0;

  const renderEditableCard = (cardTitle: string, cardBody: string) => {
    const index = cardIndex;
    cardIndex += 1;
    const titleKey = `equity_card_${index}_title`;
    const bodyKey = `equity_card_${index}_body`;
    const titleOverrideValue = overrides[titleKey];
    const bodyOverrideValue = overrides[bodyKey];
    const isEditingCardTitle = ctx?.editingKey === titleKey;
    const isEditingCardBody = ctx?.editingKey === bodyKey;

    return (
      <div className="methodology-details-simple" key={titleKey}>
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingCardTitle && !isEditingCardBody && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() => ctx?.startEditing(titleKey, titleOverrideValue ?? cardTitle)}
                >
                  Edit card title
                </button>
                <button
                  className="edit-text-button"
                  onClick={() => ctx?.startEditing(bodyKey, bodyOverrideValue ?? cardBody)}
                >
                  Edit card text
                </button>
                {(titleOverrideValue || bodyOverrideValue) && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => {
                      if (titleOverrideValue) ctx?.removeOverride(titleKey);
                      if (bodyOverrideValue) ctx?.removeOverride(bodyKey);
                    }}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingCardTitle ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter card title..."
              rows={2}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride(titleKey)}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <h5>{titleOverrideValue || cardTitle}</h5>
        )}
        {isEditingCardBody ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter card text..."
              rows={4}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride(bodyKey)}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : bodyOverrideValue ? (
          renderOverrideParagraphs(bodyOverrideValue)
        ) : (
          <p>{cardBody}</p>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="editable-section">
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingTitle && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() => ctx?.startEditing('equity_title', titleOverride ?? title)}
                >
                  Edit title
                </button>
                {titleOverride && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => ctx?.removeOverride('equity_title')}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingTitle ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter custom title..."
              rows={2}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride('equity_title')}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <h4>{titleOverride || title}</h4>
        )}
      </div>

      <div className="editable-section">
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingDescription && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() =>
                    ctx?.startEditing(
                      'equity_description',
                      descriptionOverride ?? htmlToPlainText(description)
                    )
                  }
                >
                  Edit equity text
                </button>
                {descriptionOverride && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => ctx?.removeOverride('equity_description')}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingDescription ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter custom equity text..."
              rows={6}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride('equity_description')}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : descriptionOverride ? (
          <div className="editable-section-override">{renderOverrideParagraphs(descriptionOverride)}</div>
        ) : (
          <p dangerouslySetInnerHTML={{ __html: description }} />
        )}
      </div>
      {React.Children.map(children, (child) => {
        if (!isSimpleCardElement(child)) return child;
        const { title: cardTitle, body: cardBody } = extractSimpleCardContent(child);
        return renderEditableCard(cardTitle, cardBody);
      })}
    </>
  );
};

export const MethodologyContent: React.FC<{
  title: string;
  description: string;
  dataSources: string[];
  methodology: string[];
}> = ({ title, description, dataSources, methodology }) => {
  const ctx = React.useContext(ChartTextOverridesContext);
  const overrides = ctx?.overrides || {};
  const canEdit = !!ctx?.canEditText && !!ctx?.location;

  const titleOverride = overrides.methodology_title;
  const descriptionOverride = overrides.methodology_description;
  const sourcesOverride = overrides.methodology_sources;
  const stepsOverride = overrides.methodology_steps;

  const isEditingTitle = ctx?.editingKey === 'methodology_title';
  const isEditingDescription = ctx?.editingKey === 'methodology_description';
  const isEditingSources = ctx?.editingKey === 'methodology_sources';
  const isEditingSteps = ctx?.editingKey === 'methodology_steps';

  const overriddenSources = sourcesOverride
    ? sourcesOverride.split('\n').map((item) => item.trim()).filter(Boolean)
    : dataSources;
  const overriddenSteps = stepsOverride
    ? stepsOverride.split('\n').map((item) => item.trim()).filter(Boolean)
    : methodology;

  return (
    <>
      <div className="editable-section">
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingTitle && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() => ctx?.startEditing('methodology_title', titleOverride ?? title)}
                >
                  Edit title
                </button>
                {titleOverride && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => ctx?.removeOverride('methodology_title')}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingTitle ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter custom title..."
              rows={2}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride('methodology_title')}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <h4>{titleOverride || title}</h4>
        )}
      </div>

      <div className="editable-section">
        {canEdit && (
          <div className="editable-section-controls">
            {!isEditingDescription && (
              <>
                <button
                  className="edit-text-button"
                  onClick={() =>
                    ctx?.startEditing(
                      'methodology_description',
                      descriptionOverride ?? description
                    )
                  }
                >
                  Edit methodology text
                </button>
                {descriptionOverride && (
                  <button
                    className="edit-text-secondary"
                    onClick={() => ctx?.removeOverride('methodology_description')}
                  >
                    Restore default
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {isEditingDescription ? (
          <div className="editable-section-editor">
            <textarea
              className="editable-section-textarea"
              value={ctx?.draftText || ''}
              onChange={(e) => ctx?.setDraftText(e.target.value)}
              placeholder="Enter custom methodology text..."
              rows={6}
            />
            <div className="editable-section-actions">
              <button className="edit-text-button" onClick={() => ctx?.saveOverride('methodology_description')}>
                Save
              </button>
              <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        ) : descriptionOverride ? (
          <div className="editable-section-override">{renderOverrideParagraphs(descriptionOverride)}</div>
        ) : (
          <p>{description}</p>
        )}
      </div>

      <div className="methodology-details">
        <div className="editable-section">
          {canEdit && (
            <div className="editable-section-controls">
              {!isEditingSources && (
                <>
                  <button
                    className="edit-text-button"
                    onClick={() =>
                      ctx?.startEditing(
                        'methodology_sources',
                        sourcesOverride ?? dataSources.join('\n')
                      )
                    }
                  >
                    Edit data sources
                  </button>
                  {sourcesOverride && (
                    <button
                      className="edit-text-secondary"
                      onClick={() => ctx?.removeOverride('methodology_sources')}
                    >
                      Restore default
                    </button>
                  )}
                </>
              )}
            </div>
          )}
          {isEditingSources ? (
            <div className="editable-section-editor">
              <textarea
                className="editable-section-textarea"
                value={ctx?.draftText || ''}
                onChange={(e) => ctx?.setDraftText(e.target.value)}
                placeholder="Enter one data source per line..."
                rows={6}
              />
              <div className="editable-section-actions">
                <button className="edit-text-button" onClick={() => ctx?.saveOverride('methodology_sources')}>
                  Save
                </button>
                <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <h5>Data Sources:</h5>
        <ul>
          {overriddenSources.map((source, index) => (
            <li key={index} dangerouslySetInnerHTML={{ __html: source }} />
          ))}
        </ul>

        <div className="editable-section">
          {canEdit && (
            <div className="editable-section-controls">
              {!isEditingSteps && (
                <>
                  <button
                    className="edit-text-button"
                    onClick={() =>
                      ctx?.startEditing(
                        'methodology_steps',
                        stepsOverride ?? methodology.join('\n')
                      )
                    }
                  >
                    Edit methodology steps
                  </button>
                  {stepsOverride && (
                    <button
                      className="edit-text-secondary"
                      onClick={() => ctx?.removeOverride('methodology_steps')}
                    >
                      Restore default
                    </button>
                  )}
                </>
              )}
            </div>
          )}
          {isEditingSteps ? (
            <div className="editable-section-editor">
              <textarea
                className="editable-section-textarea"
                value={ctx?.draftText || ''}
                onChange={(e) => ctx?.setDraftText(e.target.value)}
                placeholder="Enter one methodology step per line..."
                rows={6}
              />
              <div className="editable-section-actions">
                <button className="edit-text-button" onClick={() => ctx?.saveOverride('methodology_steps')}>
                  Save
                </button>
                <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <h5>Methodology:</h5>
        <ul>
          {overriddenSteps.map((item, index) => (
            <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      </div>
    </>
  );
};
